import { useEffect, useState } from "react";
import { useDebounce } from "../../../../hooks";
import { BaseSelectableItem, ValidationResult } from "../../../../types/forms";
import ScaleOption from "../../../../types/forms/ScaleOption";
import { SliderScoreDisplayType } from "../../../../types/generic";
import { Slider, SliderTheme } from "../../../common";

interface BehaviourSliderProps {
  behaviourOrAttributeId: number;
  /** Unique name for radio tile mode; must not collide across behaviours/attributes on one page */
  uniqueFieldName: string;
  /** Whether or not to display the validation warnings */
  showValidationErrors?: boolean;
  /** The slider options */
  scaleOptions: BaseSelectableItem[];
  /** Whether to show the numeric scale selected on the slider or words */
  sliderScoreDisplayType: SliderScoreDisplayType;
  isReadOnly: boolean;
  sliderValidationResult: ValidationResult;
  handleSliderChange: (
    behaviourOrAttributeId: number,
    scaleOptions: ScaleOption[]
  ) => void;

  /** An event to call when one of the inputs loses focus */
  onBlur?(
    behaviourOrAttributeId: number,
    scaleOptions: ScaleOption[]
  ): void | undefined;
  /** The theme to use for the slider */
  sliderTheme?: SliderTheme;
}

function BehaviourSlider({
  behaviourOrAttributeId,
  uniqueFieldName,
  showValidationErrors = false,
  scaleOptions,
  sliderScoreDisplayType,
  isReadOnly,
  sliderValidationResult,
  sliderTheme,
  handleSliderChange,
  onBlur,
}: BehaviourSliderProps) {
  const [valueHasBeenChanged, setValueHasBeenChanged] =
    useState<boolean>(false);
  const debouncedCurrentValue = useDebounce(scaleOptions, 500);

  useEffect(() => {
    if (valueHasBeenChanged && onBlur) {
      onBlur(behaviourOrAttributeId, debouncedCurrentValue);
      // Reset so sibling slider parent re-inits (new scaleOptions refs) do not
      // re-fire saves for sliders the user is no longer editing.
      setValueHasBeenChanged(false);
    }
  }, [debouncedCurrentValue]);

  const onSliderChange = (newState: BaseSelectableItem[]) => {
    handleSliderChange(behaviourOrAttributeId, newState);
    setValueHasBeenChanged(true);
  };

  return (
    <div className="px-4">
      <Slider
        scaleOptions={scaleOptions}
        uniqueFieldName={uniqueFieldName}
        onChange={onSliderChange}
        showValidationErrors={showValidationErrors}
        validationResult={sliderValidationResult}
        topMarginClassName="mt-2"
        selectedValueDisplayMode={sliderScoreDisplayType}
        isReadOnly={isReadOnly}
        theme={sliderTheme}
      />
    </div>
  );
}

export default BehaviourSlider;
